import { SILOREAL_WEBSITE_VERSION } from '@old/data/constants';
import { Countryside } from '@old/typed/domain/base/countryside.types';
import { Lot } from '@old/typed/domain/base/lot.types';
import { Harvest } from '@old/typed/domain/base/silobag/silobag-harvest.types';
import { Product } from '@old/typed/domain/base/silobag/silobag-product.types';
import { Species } from '@old/typed/domain/base/silobag/silobag-species.types';
import { Silobag } from '@old/typed/domain/base/silobag/silobag.types';
import { Color } from '@old/typed/domain/siloreal-service/silo-metrix/color.types';
import { Pagination } from '@old/typed/extra/pagination.types';
import { Organization } from '@old/typed/domain/base/organization.types';
import { DashboardInfoData } from '@old/services/domain/page/dashboard/dashboard-info.types';
import { User } from '@old/typed/domain/user.types';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { SILOREAL_LOCALSTORAGE_KEY } from '../data/constants/localstorage-keys';

type SilobagListFilters = {
  countryside_id: Countryside['id'][];
  lot_id: Lot['id'] | string;
  silobag_species_type_id: Species['id'][];
  silobag_product_type_id: Product['id'] | string;
  silobag_harvest_id: Harvest['id'] | string;
  sat_view_active: boolean | string;
  corvus: boolean | string;
  inconsistencies: boolean | string;
  is_silo_metrix_active: boolean | string;
  color_id: Color['id'] | string;
  name: Silobag['name'];
  from: string;
  to: string;
  range: string;
  silobag_data_status_id: Silobag['status_id'] | string;
  silobag_data_statuses?: number;
  active: Silobag['active'];
  paginationModel: { page: Pagination['page']; pageSize: Pagination['take'] };
  countryside_zone_id: string;
  countryside_zone_name: string;
  tag_ids: number[];
};

type AvailablePermissions = { read: boolean; write: boolean; full: boolean };
type OrgPermissions = { organization: { id: Organization['id'] } & AvailablePermissions };
type CountrysidePermissions = { countryside: ({ id: Countryside['id'] } & AvailablePermissions)[] };
type Permissions = OrgPermissions & CountrysidePermissions;

type AwsCredentials = { AccessKeyId: string; SecretAccessKey: string; SessionToken: string };

// Evitar referencia circular: usar el tipo directamente en lugar de ReturnType
type DashboardCountrysides = DashboardInfoData['countrysides'];

type PaginationModel = { page: number; pageSize: number };
type StockReportFilters = {
  silobagId?: Silobag['id'] | '';
  silobagName?: Silobag['name'] | '';
  countrysideId: Countryside['id'] | '';
  countrysideName: Countryside['name'] | '';
  startDate?: string;
  endDate?: string;
};
type DevicesReportFilters = {
  lance_number: string;
  countrysideId?: Countryside['id'] | string;
  silobagId: Silobag['id'] | '';
  installed?: boolean | string;
  report?: boolean | string;
  paginationModel: PaginationModel;
};

type StockStateReportFilters = {
  name: string;
  countrysideId: Countryside['id'] | string;
  lotId: Lot['id'] | string;
  paginationModel: PaginationModel;
};

type ExistenceReportFilters = {
  countrysideId: Countryside['id'] | string;
  specieId: Species['id'] | string;
  paginationModel: PaginationModel;
};

type ActivityReportFilters = {
  silobagId?: Silobag['id'] | '';
  countrysideId: Countryside['id'] | '';
  countrysideZoneId: Countryside['id'] | '';
  countrysideZoneName: string | '';
  userId: User['id'] | '';
  typeId: string;
  paginationModel: PaginationModel;
};

type InconsistenciesFilters = {
  silobagId: string;
  silobagName: string;
  countrysidesIds: number[];
  levelIds: number[];
  typeCodes: string[];
  ruleIds: number[];
  solved: boolean;
  organizationId?: number;
};

type GlobalStore = {
  preferedLanguageId: string;
  accessToken: string | undefined;
  verifyAccessToken: string | undefined;
  refreshToken: string | undefined;
  permissions?: Permissions;
  expirationTokenDate: number;
  email: string | undefined;
  //la del selector variable a cambios TODO: cambiar nombre de variable por selectorOrganizationId
  organizationId: number | undefined;
  // este userOrganizationId es el que se usa para almacenar siempre la organizacion del usuario, el otro organizationId es el que se usa en el selector
  userOrganizationId: number | undefined;
  was_tutorial_shown: boolean | undefined;
  userID: number | undefined;
  //Organizacion tomada en el login
  organizationName: string | undefined;
  //Organizacion seleccionada en el selector
  selectedOrganizationName: string | undefined;
  // Change snake case
  was_lasegunda_integration_popup_shown: boolean | undefined;
  was_rus_popup_shown: boolean | undefined;
  was_galicia_popup_shown: boolean | undefined;
  was_fed_patronal_popup_shown: boolean | undefined;
  was_agrotoken_integration_popup_shown: boolean | undefined;
  was_warrant_popup_shown: boolean | undefined;
  was_swissre_popup_shown: boolean | undefined;
  ishowOneModal: boolean;
  userFullname: string | undefined;
  awsTempCredentials?: AwsCredentials;
  organizationTaxId: string | undefined;
  organizationLogo: string | null;
  countrysides?: DashboardCountrysides;
  userPhoto?: string | undefined;
  // [FILTROS] Listado de Silobolsa
  filters: SilobagListFilters;
  filteredUrl: string;
  setFilters: (filters: SilobagListFilters) => void;
  setFilteredUrl: (filteredUrl: string) => void;
  // ----------------------------------------------//

  // [FILTROS] Páginas de reporte
  filtersReportStock: StockReportFilters;
  filtersReportDevices: DevicesReportFilters;
  filtersReportStateStock: StockStateReportFilters;
  filtersReportExistence: ExistenceReportFilters;
  filtersReportActivity: ActivityReportFilters;
  filtersInconsistencies: InconsistenciesFilters;
  setFiltersReportStock: (filters: StockReportFilters) => void;
  setFiltersReportDevices: (filters: DevicesReportFilters) => void;
  setFiltersReportStateStock: (filters: StockStateReportFilters) => void;
  setFiltersReportExistence: (filters: ExistenceReportFilters) => void;
  setFiltersReportActivity: (filters: ActivityReportFilters) => void;
  // ----------------------------------------------//

  country_id: number | undefined;
  isUserOnline: boolean | undefined;
  corvusIntegrated: boolean | undefined;
  totalDiscrepancies: number;
  setIsUserOnline: (isOnline: boolean) => void;
  setCountryId: (countryId: number) => void;
  setLanguage: (preferedLanguageId: string) => void;
  setCountrysides: (countrysidesList: DashboardCountrysides) => void;
  setOrganizationLogo: (logoUrl: string | null) => void;
  setAccessToken: (token: string) => void;
  setVerifyAccessToken: (token: string) => void;
  setRefreshToken: (token: string) => void;
  setPermissions: (permissions: Permissions) => void;
  setAwsTempCredentials: (awsTempCredentials: AwsCredentials) => void;
  setExpirationTokenDate: (exp: number) => void;
  setEmail: (email: string | undefined) => void;
  setOrganizationId: (organizationId: number | undefined) => void;
  setUserOrganizationId: (userOrganizationId: number | undefined) => void;
  setWasTutorialShow: (was_tutorial_shown: boolean) => void;
  setUserFullname: (userFullname: string) => void;
  setOrganizationName: (organizationName: string) => void;
  setSelectedOrganizationName: (selectedOrganizationName: string | undefined) => void;
  setOrganizationTaxId: (organizationTaxId: string) => void;
  // Change snake case
  setWas_lasegunda_integration_popup_shown: (was_lasegunda_integration_popup_shown: boolean) => void;
  setWas_agrotoken_integration_popup_shown: (was_agrotoken_integration_popup_shown: boolean) => void;
  setWas_warrant_popup_shown: (was_warrant_popup_shown: boolean) => void;
  setIshowOneModal: (showOneModal: boolean) => void;
  setWas_rus_popup_shown: (was_rus_popup_shown: boolean) => void;
  setWas_galicia_popup_shown: (was_galicia_popup_shown: boolean) => void;
  setWas_fed_patronal_popup_shown: (was_fed_patronal_popup_shown: boolean) => void;
  setWas_swissre_popup_shown: (was_swissre_popup_shown: boolean) => void;
  setUserID: (userID: number) => void;
  clearAuthData: () => void;
  // setTypeOfProduct: (types: { id: number; name: string }[]) => void;
  setCorvusIntegrated: (isIntegrated: boolean) => void;
  setUserPhoto: (photo: string) => void;
  setTotalDiscrepancies: (totalDiscrepancies: number) => void;
  setFiltersInconsistencies: (filters: InconsistenciesFilters) => void;
};

export const useGlobalStore = create<GlobalStore>()(
  persist(
    (set) => ({
      preferedLanguageId: '',
      accessToken: undefined,
      verifyAccessToken: undefined,
      refreshToken: undefined,
      permissions: undefined,
      expirationTokenDate: 0,
      email: undefined,
      organizationId: undefined,
      userOrganizationId: undefined,
      was_tutorial_shown: undefined,
      userID: undefined,
      userFullname: undefined,
      was_lasegunda_integration_popup_shown: undefined,
      was_agrotoken_integration_popup_shown: undefined,
      was_warrant_popup_shown: undefined,
      was_rus_popup_shown: undefined,
      was_galicia_popup_shown: undefined,
      was_fed_patronal_popup_shown: undefined,
      was_swissre_popup_shown: undefined,
      ishowOneModal: false,
      organizationName: undefined,
      awsTempCredentials: undefined,
      organizationTaxId: undefined,
      organizationLogo: null,
      countrysides: undefined,
      country_id: undefined,
      selectedOrganizationName: undefined,
      userPhoto: undefined,
      filters: {
        countryside_id: [],
        lot_id: '',
        silobag_species_type_id: [],
        silobag_product_type_id: '',
        silobag_harvest_id: '',
        sat_view_active: '',
        is_silo_metrix_active: '',
        color_id: '',
        inconsistencies: '',
        corvus: '',
        name: '',
        from: '',
        to: '',
        range: '',
        active: true,
        silobag_data_status_id: 2,
        paginationModel: {
          page: 0,
          pageSize: 50,
        },
        countryside_zone_id: '',
        countryside_zone_name: '',
        tag_ids: [],
        // archived: false, // TODO: consultar con Víctor si esta línea puede ser completamente aniquilada.
      },
      filteredUrl: '',
      filtersReportStock: {
        startDate: '',
        endDate: '',
        countrysideId: '',
        countrysideName: '',
        silobagId: '',
        silobagName: '',
      },
      filtersReportDevices: {
        lance_number: '',
        countrysideId: undefined,
        silobagId: '',
        installed: undefined,
        report: undefined,
        paginationModel: {
          page: 0,
          pageSize: 50,
        },
      },
      filtersReportStateStock: {
        name: '',
        countrysideId: '',
        lotId: '',
        paginationModel: {
          page: 0,
          pageSize: 50,
        },
      },
      filtersReportExistence: {
        countrysideId: '',
        specieId: '',
        paginationModel: {
          page: 0,
          pageSize: 50,
        },
      },
      filtersReportActivity: {
        countrysideId: '',
        userId: '',
        typeId: '',
        paginationModel: {
          page: 0,
          pageSize: 50,
        },
        countrysideZoneId: '',
        countrysideZoneName: '',
      },
      filtersInconsistencies: {
        silobagId: '',
        silobagName: '',
        countrysidesIds: [],
        levelIds: [],
        ruleIds: [],
        typeCodes: [],
        solved: false,
      },
      // typeOfProduct: [],
      isUserOnline: false,
      corvusIntegrated: undefined,
      totalDiscrepancies: 0,
      setCorvusIntegrated: (isIntegrated: boolean) => set({ corvusIntegrated: isIntegrated }),
      setIsUserOnline: (isOnline: boolean) => set({ isUserOnline: isOnline }),
      setCountryId: (countryId: number) => set({ country_id: countryId }),
      setLanguage: (newLanguage: string) => set({ preferedLanguageId: newLanguage }),
      setCountrysides: (countrysidesList: DashboardCountrysides) => set({ countrysides: countrysidesList }),
      setOrganizationLogo: (organizationLogo: string | null) => set({ organizationLogo: organizationLogo }),
      setAccessToken: (token: string) => set({ accessToken: token }),
      setVerifyAccessToken: (token: string) => set({ verifyAccessToken: token }),
      setRefreshToken: (token: string) => set({ refreshToken: token }),
      setPermissions: (permissions: Permissions) => set({ permissions }),
      setAwsTempCredentials: (awsTempCredentials: AwsCredentials) => set({ awsTempCredentials }),
      setExpirationTokenDate: (exp: number) => set({ expirationTokenDate: exp }),
      setEmail: (email: string | undefined) => set({ email: email }),
      setOrganizationId: (organizationId: number | undefined) => set({ organizationId: organizationId }),
      setUserOrganizationId: (userOrganizationId: number | undefined) =>
        set({ userOrganizationId: userOrganizationId }),
      setUserID: (userID: number) => set({ userID: userID }),
      setWasTutorialShow: (was_tutorial_shown: boolean) => set({ was_tutorial_shown: was_tutorial_shown }),
      setUserFullname: (userFullname: string) => set({ userFullname: userFullname }),
      setOrganizationName: (organizationName: string) => set({ organizationName: organizationName }),
      setSelectedOrganizationName: (selectedOrganizationName: string | undefined) =>
        set({ selectedOrganizationName: selectedOrganizationName }),
      setOrganizationTaxId: (organizationTaxId: string) => set({ organizationTaxId: organizationTaxId }),
      setFilters: (filters: SilobagListFilters) => set({ filters: filters }),
      setFilteredUrl: (filteredUrl: string) => set({ filteredUrl: filteredUrl }),
      setFiltersReportStock: (filters: StockReportFilters) => set({ filtersReportStock: filters }),
      setFiltersReportDevices: (filters: DevicesReportFilters) => set({ filtersReportDevices: filters }),
      setFiltersReportStateStock: (filters: StockStateReportFilters) =>
        set({ filtersReportStateStock: filters }),
      setFiltersReportExistence: (filters: ExistenceReportFilters) =>
        set({ filtersReportExistence: filters }),
      setFiltersInconsistencies: (filters: InconsistenciesFilters) =>
        set({ filtersInconsistencies: filters }),
      // setCountrysidesSelected: (countrysidesSelected) => set({ countrysidesID: countrysidesSelected }),
      // setSpeciesSelected: (speciesSelected) => set({ speciesID: speciesSelected }),
      //actividad
      setFiltersReportActivity: (filters: ActivityReportFilters) => set({ filtersReportActivity: filters }),
      // setTypeOfProduct: (types) => set({ typeOfProduct: types }),
      // setSiloSatActive: (siloSatActive) => set({ siloSatActive: siloSatActive }),
      // setSiloMetrixActive: (siloMetrixActive) => set({ siloMetrixActive: siloMetrixActive }),
      setWas_lasegunda_integration_popup_shown: (was_lasegunda_integration_popup_shown: boolean) =>
        set({ was_lasegunda_integration_popup_shown }),
      setWas_warrant_popup_shown: (was_warrant_popup_shown: boolean) => set({ was_warrant_popup_shown }),
      setWas_rus_popup_shown: (was_rus_popup_shown: boolean) => set({ was_rus_popup_shown }),
      setWas_galicia_popup_shown: (was_galicia_popup_shown: boolean) => set({ was_galicia_popup_shown }),
      setWas_fed_patronal_popup_shown: (was_fed_patronal_popup_shown: boolean) =>
        set({ was_fed_patronal_popup_shown }),
      setWas_swissre_popup_shown: (was_swissre_popup_shown: boolean) =>
        set({ was_swissre_popup_shown: was_swissre_popup_shown }),
      setIshowOneModal: (showOneModal: boolean) => set({ ishowOneModal: showOneModal }),
      setWas_agrotoken_integration_popup_shown: (was_agrotoken_integration_popup_shown: boolean) =>
        set({ was_agrotoken_integration_popup_shown: was_agrotoken_integration_popup_shown }),
      setUserPhoto: (photo: string) => set({ userPhoto: photo }),
      setTotalDiscrepancies: (totalDiscrepancies: number) => set({ totalDiscrepancies: totalDiscrepancies }),
      clearAuthData: () =>
        set({
          accessToken: undefined,
          refreshToken: undefined,
          permissions: undefined,
          expirationTokenDate: 0,
          awsTempCredentials: undefined,
          // typeOfProduct: [],
          // siloSatActive: [],
          // siloMetrixActive: [],
          // countrysidesID: [],
          // speciesID: [],
          userPhoto: undefined,
          selectedOrganizationName: undefined,
          // Se agrega el clear de los filtros del listado de silobolsa hasta detectar un bug relacionado (la idea no es dejarlo acá)
          filters: {
            // TODO: borrar cuando se detecte y resuelva el bug
            countryside_id: [],
            lot_id: '',
            silobag_species_type_id: [],
            silobag_product_type_id: '',
            silobag_harvest_id: '',
            sat_view_active: '',
            is_silo_metrix_active: '',
            color_id: '',
            corvus: '',
            name: '',
            from: '',
            to: '',
            range: '',
            inconsistencies: '',
            silobag_data_status_id: 2,
            active: true,
            paginationModel: {
              page: 0,
              pageSize: 50,
            },
            countryside_zone_id: '',
            countryside_zone_name: '',
            tag_ids: [],
          },
          filteredUrl: '', // TODO: borrar cuando se detecte y resuelva el bug
          ishowOneModal: false,
          filtersReportStock: {
            startDate: '',
            endDate: '',
            countrysideId: '',
            countrysideName: '',
            silobagId: '',
            silobagName: '',
          },
          filtersReportDevices: {
            lance_number: '',
            countrysideId: undefined,
            silobagId: '',
            installed: undefined,
            report: undefined,
            paginationModel: {
              page: 0,
              pageSize: 50,
            },
          },
          filtersReportStateStock: {
            name: '',
            countrysideId: '',
            lotId: '',
            paginationModel: {
              page: 0,
              pageSize: 50,
            },
          },
          filtersReportExistence: {
            countrysideId: '',
            specieId: '',
            paginationModel: {
              page: 0,
              pageSize: 50,
            },
          },
          filtersReportActivity: {
            countrysideId: '',
            userId: '',
            typeId: '',
            paginationModel: {
              page: 0,
              pageSize: 50,
            },
            countrysideZoneId: '',
            countrysideZoneName: '',
          },
          filtersInconsistencies: {
            silobagId: '',
            silobagName: '',
            countrysidesIds: [],
            levelIds: [],
            typeCodes: [],
            ruleIds: [],
            solved: false,
            organizationId: undefined,
          },
          totalDiscrepancies: 0,
        }),
    }),

    { name: SILOREAL_LOCALSTORAGE_KEY, version: parseInt(SILOREAL_WEBSITE_VERSION.replaceAll('.', ''), 10) }
  )
);

// NOTE: Do not call useGlobalStore.setState here to set defaults.
// Defaults are already defined in the state creator above and persisted state
// will be rehydrated asynchronously by the persist middleware.
